<template>
    <div class="table-headers">
        <div class="table-header-cell record-id-header">
            <a-checkbox
                :checked="isAllSelected"
                @change="$emit('toggle-select-all', $event.target.checked)"
            ></a-checkbox>
        </div>
        <draggable :list="$_.cloneDeep(visibleFields)" @end="onDragEnd" class="drag-context-wrapper">
            <div
                v-for="field in visibleFields"
                :key="field._id"
                class="table-header-cell"
                :class="{ selected: selectedField === field, identifier: field.isIdentifierField }"
                @click="selectField(field)"
            >
                <a-popover trigger="click" placement="bottomLeft">
                    <template slot="content">
                        <div class="action-list field-actions-menu">
                            <div class="action-item" @click="handleAction('editField', field)" v-if="!moduleMode">
                                <i class="material-icons">edit</i>
                                <span>{{ $t("tables.editField") }}</span>
                            </div>
                            <a-divider v-if="!moduleMode" />
                            <div class="action-group">
                                <div class="group-name">{{ $t("tables.sort") }}</div>
                                <div
                                    class="action-item"
                                    :class="{ disabled: field.valueType === 'array' }"
                                    @click="field.valueType !== 'array' && handleAction('sortAscending', field)"
                                >
                                    <i class="material-icons">arrow_upward</i>
                                    <span>{{ $t("tables.ascending") }}</span>
                                </div>
                                <div
                                    class="action-item"
                                    :class="{ disabled: field.valueType === 'array' }"
                                    @click="field.valueType !== 'array' && handleAction('sortDescending', field)"
                                >
                                    <i class="material-icons">arrow_downward</i>
                                    <span>{{ $t("tables.descending") }}</span>
                                </div>
                                <div
                                    class="action-item"
                                    @click="handleAction('makeIdentifier', field)"
                                    v-if="!field.isIdentifierField && isValidForIdentifier(field)"
                                    >
                                    <i class="material-icons">check_circle</i>
                                    <span>{{ $t("Mark as Identifier Field") }}</span>
                                </div>
                            </div>
                            <a-divider />
                            <div class="action-group" v-if="false">
                                <div class="group-name">{{ $t("tables.createField") }}</div>
                                <div class="action-item" @click="handleAction('insertBefore', field)">
                                    <i class="material-icons">add</i>
                                    <span>{{ $t("tables.insertBefore") }}</span>
                                </div>
                                <div class="action-item" @click="handleAction('insertAfter', field)">
                                    <i class="material-icons">add</i>
                                    <span>{{ $t("tables.insertBefore") }}</span>
                                </div>
                            </div>
                            <a-divider v-if="false" />
                            <div class="action-group">
                                <div class="group-name">{{ $t("ticimax.automation") }}</div>
                                <div
                                    class="action-item"
                                    @click="handleAction('gotoAutomation', field)"
                                    v-if="field.fieldType === 'automation-button'"
                                >
                                    <component is="atomic-power" class="myIcon" />
                                    <span>{{ $t("tables.goToRelatedAuto") }}</span>
                                </div>
                                <div class="action-item" @click="handleAction('createAutomation', field)">
                                    <i class="material-icons">settings_suggest</i>
                                    <span>{{ $t("ticimax.createAutomation") }}</span>
                                </div>
                            </div>
                            <a-divider />
                            <div class="action-group">
                                <div class="group-name">{{ $t("tables.permission") }}</div>
                                <div class="action-item" @click="handleAction('lockField', field)">
                                    <i class="material-icons">lock</i>
                                    <span>{{ $t("tables.lockField") }}</span>
                                </div>
                                <div class="action-item" @click="handleAction('hideField', field)">
                                    <i class="material-icons">visibility_off</i>
                                    <span>{{ $t("tables.hideField") }}</span>
                                </div>
                            </div>
                            <a-divider />
                            <div class="action-item" @click="handleAction('deleteField', field)" v-if="!moduleMode">
                                <i class="material-icons">delete</i>
                                <span>{{ $t("tables.deleteField") }}</span>
                            </div>
                        </div>
                    </template>
                    <div class="table-header-cell-content">
                        <FieldIcon :type="field.fieldType" class="mr-2" />
                        <span>{{ field.name }}</span>
                    </div>
                </a-popover>
            </div>
        </draggable>
        <!-- "Add Field" hücresi ve popover -->
        <div class="table-header-cell add-field-cell" v-if="!moduleMode">
            <a-popover v-model="isAddFieldPopoverVisible" trigger="click" placement="bottomRight">
                <template slot="content">
                    <FieldSelector @field-selected="openFieldCreator" @close="addFieldPopoverVisible = false" />
                </template>
                <span>+ {{ $t("calendar.addField") }}</span>
            </a-popover>
        </div>

        <!-- FieldCreator'ı Modal2 içinde göster -->
        <Modal2
            v-if="isFieldCreatorVisible"
            :title="fieldCreatorMode === 'create' ? $t('tables.createNewField') : 'Alanı Düzenle'"
            @cancel="closeFieldCreator"
            :showFooter="false"
            className="field-creator-modal keydown-prevent-modal"
        >
            <FieldCreator
                :defaultFieldType="selectedFieldType"
                :table-id="table._id"
                :mode="fieldCreatorMode"
                :field="selectedField"
                @field-created-success="onFieldCreated"
                @field-updated-success="onFieldUpdated"
                @cancel="closeFieldCreator"
            />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import FieldSelector from "./components/FieldSelector.vue";
import FieldCreator from "./components/FieldCreator.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import { TABLE_API } from "../../../../api/table";
import draggable from "vuedraggable";
import log2 from "../../../../utils/log2";
import FieldIcon from "../FieldIcon.vue";
import { mapActions } from "vuex";
const log = log2("TableHeaders");
export default Vue.extend({
    name: "TableHeaders",
    components: {
        FieldSelector,
        FieldCreator,
        Modal2,
        draggable,
        FieldIcon,
    },
    props: {
        table: {
            type: Object,
            required: true,
        },
        isSelectionModeActive: Boolean,
        selectedRecords: Array,
        isAllSelected: Boolean,
        visibleFields: {
            type: Array,
            required: true,
        },
        moduleMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isFieldCreatorVisible: false,
            isAddFieldPopoverVisible: false,
            selectedFieldType: null,
            selectedField: null,
            fieldCreatorMode: "create",
        };
    },
 
    methods: {
        ...mapActions({
            syncTables: "table/syncTables",
        }),
        isIdentifierField(field) {
            return field.isIdentifierField;
        },
        isValidForIdentifier(field) {
            //valueType string, number  
            // or fieldType contact ,  user , linked-record
            return ["string", "number"].includes(field.valueType) || ["contact", "user", "linked-record"].includes(field.fieldType);
        },
        move(event) {
            log("move", event);
            return false;
        },
        openFieldCreator(fieldType) {
            this.isAddFieldPopoverVisible = false;
            this.$feature.beforeCreateTableField(() => {
                this.selectedFieldType = fieldType;
                this.isFieldCreatorVisible = true;
                this.fieldCreatorMode = "create";
                this.selectedField = null;
            }, this.table.totalFields);
        },
        closeFieldCreator() {
            // Modal'ı kapat ve seçili fieldType'ı sıfırla
            this.isFieldCreatorVisible = false;
            this.selectedFieldType = null;
            // Popover'ı kapat
            this.isAddFieldPopoverVisible = false;
        },
        onFieldCreated() {
            // Üst componente tabloyu yenilemesini bildir
            this.$emit("refresh-table");
            // Modal'ı kapat
            this.closeFieldCreator();
        },
        handleAction(action, field) {
            switch (action) {
                case "editField":
                    this.selectedField = field;
                    this.fieldCreatorMode = "update";
                    this.isFieldCreatorVisible = true;
                    break;
                case "sortAscending":
                    this.$emit("sort-changed", {
                        field: field._id,
                        direction: "asc",
                        valueName: `${field.name}Value`,
                    });
                    break;
                case "sortDescending":
                    this.$emit("sort-changed", {
                        field: field._id,
                        direction: "desc",
                        valueName: `${field.name}Value`,
                    });
                    break;
                case "insertBefore":
                    // Insert before logic
                    break;
                case "insertAfter":
                    // Insert after logic
                    break;
                case "createAutomation":
                    // Create automation logic
                    break;
                case "lockField":
                    // Lock field logic
                    break;
                case "hideField":
                    this.$emit("hide-field", field._id);
                    break;
                case "deleteField":
                    this.confirmDeleteField(field);
                    break;
                case "gotoAutomation":
                    const automationId = field.options.button.automation;
                    this.$router.push(`/automations/${automationId}/design`);
                    break;
                case "makeIdentifier":
                    this.makeIdentifier(field);
                    break;
            }
        },
        selectField(field) {
            this.selectedField = field;
        },
        async confirmDeleteField(field) {
            try {
                const confirmed = await this.$confirm({
                    title: "Alanı Sil",
                    content: `"${field.name}" alanını silmek istediğinize emin misiniz?`,
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: async () => {
                        await TABLE_API.deleteField(this.table._id, field._id);
                        this.$emit("refresh-table");
                        this.$message.success("Alan başarıyla silindi.");
                        this.syncTables();
                    },
                });
            } catch (error) {
                if (error.message !== "cancel") {
                    log.error("Alanı silerken hata oluştu:", error);
                    this.$message.error("Alanı silerken bir hata oluştu.");
                }
            }
        },
        onFieldUpdated(updatedField) {
            const index = this.visibleFields.findIndex((field) => field._id === updatedField._id);
            if (index !== -1) {
                this.$set(this.visibleFields, index, updatedField);
            }
            this.closeFieldCreator();
        },
        async onDragEnd(event) {
            log(`drag from ${event.oldIndex} to ${event.newIndex}`);
            const movingField = this.visibleFields[event.oldIndex];
            const toField = this.visibleFields[event.newIndex];

            const oldPositionFieldNames = this.table.fields.map((x) => `${x.name} (${x.position})`);
            let fields = this.table.fields.map((x) => ({ name: x.name, pos: x.position, _id: x._id }));
            fields = _.orderBy(fields, ["pos"], ["asc"]);
            fields = fields.map((x, i) => ({ ...x, pos: i }));
            const movedFieldIndex = fields.findIndex((x) => x._id === movingField._id);
            const toFieldIndex = fields.findIndex((x) => x._id === toField._id);
            log("movingField", movingField.name, " index: ", movedFieldIndex);
            log("toField", toField.name, " index: ", toFieldIndex);
            fields.splice(movedFieldIndex, 1);
            fields.splice(toFieldIndex, 0, { name: movingField.name, pos: toField.position, _id: movingField._id });
            fields = fields.map((x, i) => ({ ...x, pos: i + 1 }));
            fields = _.sortBy(fields, ["pos"]);
            const newPositionFieldNames = fields.map((x) => `${x.name} (${x.pos})`);

            log("oldPositionFieldNames", oldPositionFieldNames);
            log("newPositionFieldNames", newPositionFieldNames);
            log("fields", fields);
            try {
                const positions = fields.reduce((acc, cur) => {
                    acc[cur._id] = cur.pos;
                    return acc;
                }, {});
                await TABLE_API.updateTableFieldPositions(this.table._id, positions);
                console.log("Alan pozisyonları başarıyla güncellendi.");
                this.$emit("positions-changed", positions);
            } catch (error) {
                console.error("Alan pozisyonları güncellenirken bir hata oluştu:", error);
            }
        },
        async makeIdentifier(field) {
            try {
                // Önce mevcut identifier field'ı false yap
                const currentIdentifierField = this.visibleFields.find(f => f.isIdentifierField);
                if (currentIdentifierField) {
                    await TABLE_API.updateField(this.table._id, currentIdentifierField._id, {
                        isIdentifierField: false
                    });
                }

                // Seçilen field'ı identifier olarak işaretle
                await TABLE_API.updateField(this.table._id, field._id, {
                    isIdentifierField: true
                });

                this.$message.success(`${field.name} başarıyla tanımlayıcı alan olarak ayarlandı.`);
                this.$emit("refresh-table");
                this.syncTables();
                
            } catch (error) {
                console.error("Tanımlayıcı alan güncellenirken hata oluştu:", error);
                this.$message.error("Tanımlayıcı alan güncellenirken bir hata oluştu.");
            }
        },
    },
});
</script>

<style scoped lang="scss">
.table-headers {
    width: max-content;
    display: flex;
    border-top: 1px solid #ccc;
    background: #e0e2e9;
    position: sticky;
    top: 0px;
    z-index: 99;

    .drag-context-wrapper {
        display: flex;
        width: max-content;
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: -ms-autohiding-scrollbar;

        &::-webkit-scrollbar {
            display: none;
        }
    }
}

.table-header-cell {
    width: 245px;
    min-width: 245px;
    padding: 0 15px;
    height: 40px;
    display: flex;
    align-items: center;
    border-right: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    cursor: pointer;

    &.identifier {
        background: #beffbd !important;
        
    }

    &.selected {
        border: 2px solid #1890ff;
    }

    &:last-child {
        border-right: none;
    }

    .table-header-cell-content {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        font-weight: 800;
        color: #262626;
    }
}

.add-field-cell {
    color: #007bff;
    cursor: pointer;
}

.material-icons {
    font-size: 18px;
}

.record-id-header {
    justify-content: center;
}

.action-list {
    min-width: 200px;

    .action-item {
        display: flex;
        align-items: center;
        padding: 8px;
        cursor: pointer;

        &:hover {
            background-color: #f0f0f0;
        }

        i {
            margin-right: 8px;
        }
    }

    .action-group {
        .group-name {
            font-weight: bold;
            margin-bottom: 4px;
        }
    }
}
</style>
<style lang="scss">
.field-actions-menu {
    .ant-divider-horizontal {
        margin: 5px 0;
    }
}

.action-item {
    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
            background-color: transparent;
        }
    }
}
</style>
